import React, { useCallback, useEffect, useState } from "react";
import AuthContainer from "../../containers/Auth";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../../service/validation/signup";
import AuthInputText from "../../components/AuthInput";
import { ApiService } from "../../service/api.service";
import { AUTH_URL } from "../../common/common";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../service/firebase/firebase";

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier | null;
  }
}

const SignUp = () => {
  const navigate = useNavigate();
  const [emailAlreadyInUse, setEmailAlreadyInUse] = useState<boolean>(false);
  const [phoneAlreadyInUse, setPhoneAlreadyInUse] = useState<boolean>(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  // Create API service instance just once, not on every render
  const apiService = new ApiService();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      organization: "",
      emailId: "",
      password: "",
      mobileNumber: "",
      confirmPassword: "",
    },
    mode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  const initializeRecaptcha = useCallback(() => {
    try {
      if (!window.recaptchaVerifier) {
        const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "normal",
          callback: () => {
            console.log("reCAPTCHA solved");
            setIsCaptchaVerified(true);
          },
          "expired-callback": () => {
            if (window.recaptchaVerifier) {
              window.recaptchaVerifier.clear();
              window.recaptchaVerifier = null;
              setIsCaptchaVerified(false);
            }
            initializeRecaptcha();
          },
        });
        window.recaptchaVerifier = verifier;
        verifier.render();
      }
    } catch (error) {
      console.error("Error initializing reCAPTCHA:", error);
    }
  }, []);

  useEffect(() => {
    initializeRecaptcha();
  }, [initializeRecaptcha]);

  const onSubmit = async (data: any) => {
    if (emailAlreadyInUse || phoneAlreadyInUse) {
      return;
    }

    try {
      if (!window.recaptchaVerifier) {
        throw new Error("reCAPTCHA not initialized. Please refresh the page.");
      }

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        `+91${phoneNumberValue}`,
        window.recaptchaVerifier
      );

      if (confirmationResult) {
        localStorage.setItem("motsop-verficationId", confirmationResult.verificationId);
      }

      const response: any = await apiService.postApi(`${AUTH_URL}/signin-route`, data);

      if (response?.status === "200") {
        const { entityid, email } = response.body.data;

        if (entityid && email) {
          localStorage.setItem("entity_id", entityid);
          localStorage.setItem("email", email);
          localStorage.setItem("phoneNumber", phoneNumberValue);
          navigate("/otp-verfication", { state: { entityId: entityid, emailId: email } });
        }
      } else {
        console.error("Invalid response from /signin-route API");
      }
    } catch (err: any) {
      console.error("Error during signup:", err.message || err);

      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
        setIsCaptchaVerified(false);
      }
      initializeRecaptcha();
    }
  };

  const emailValue = watch("emailId", "");
  const phoneNumberValue = watch("mobileNumber", "");
  const passwordValue = watch("password", "");
  const firstNameValue = watch("firstName", "");
  const lastNameValue = watch("lastName", "");
  const confirmPassword = watch("confirmPassword", "");
  const organization = watch("organization", "");

  // Check if all required fields are filled
  const allFieldsFilled = Boolean(
    firstNameValue &&
    lastNameValue &&
    phoneNumberValue &&
    emailValue &&
    organization &&
    passwordValue &&
    confirmPassword
  );

  // Button should be enabled only when all conditions are met
  const isButtonEnabled = allFieldsFilled && isCaptchaVerified && isValid && !emailAlreadyInUse && !phoneAlreadyInUse;

  // Email validation check with proper dependencies
  useEffect(() => {
    // Skip the API call if email is empty or invalid
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailValue || !emailRegex.test(emailValue)) {
      setEmailAlreadyInUse(false);
      return;
    }

    let emailDebounceTimer: NodeJS.Timeout;

    const checkEmail = async () => {
      try {
        const response: any = await apiService.postApiSecond(`${AUTH_URL}/signin-route`, {
          emailId: emailValue,
          verified: true
        });

        if (response?.status === "200") {
          setEmailAlreadyInUse(false);
          clearErrors("emailId");
        } else {
          setEmailAlreadyInUse(true);
          setError("emailId", {
            type: "manual",
            message: "Email already in use"
          });
        }
      } catch (error) {
        console.error("Error checking email:", error);
      }
    };

    // Use debounce to prevent excessive API calls
    emailDebounceTimer = setTimeout(checkEmail, 500);

    return () => {
      clearTimeout(emailDebounceTimer);
    };
  }, [emailValue, clearErrors, setError]);

  // Phone validation check with proper dependencies
  useEffect(() => {
    // Skip the API call if phone number is empty or invalid
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneNumberValue || !phoneRegex.test(phoneNumberValue)) {
      setPhoneAlreadyInUse(false);
      return;
    }

    let phoneDebounceTimer: NodeJS.Timeout;

    const checkPhoneNumber = async () => {
      try {
        const response: any = await apiService.postApiSecond(`${AUTH_URL}/signin-route`, {
          mobileNumber: phoneNumberValue,
          verified: true
        });

        if (response?.status === "200") {
          setPhoneAlreadyInUse(false);
          clearErrors("mobileNumber");
        } else {
          setPhoneAlreadyInUse(true);
          setError("mobileNumber", {
            type: "manual",
            message: "Phone number already in use"
          });
        }
      } catch (error) {
        console.error("Error checking phone number:", error);
      }
    };

    // Use debounce to prevent excessive API calls
    phoneDebounceTimer = setTimeout(checkPhoneNumber, 500);

    return () => {
      clearTimeout(phoneDebounceTimer);
    };
  }, [phoneNumberValue, clearErrors, setError]);

  return (
    <AuthContainer>
      <div className="flex w-full rounded-[21.25px] min-h-[90vh] bg-white font-poppins">
        <div className="w-full h-full">
          <div className="md:mx-[4rem] py-4">
            <div className="h-[100px] w-[239px] bg-[#D9D9D9] flex items-center justify-center">
              logo
            </div>
            <br />
            <p className="font-[700] text-[20px] font-poppins leading-[] track-[1%]">
              Welcome Back 👋
            </p>
            <p className="font-[400] font-poppins text-[16px] leading-[] track-[1%] text-[#313957]">
              Lets Get You Started
            </p>
            <br />
            <form onSubmit={handleSubmit(onSubmit)} className="">
              <div className="flex flex-col md:flex-row md:gap-2">
                <AuthInputText
                  label="First Name"
                  value={firstNameValue}
                  errors={errors}
                  placeholder="First Name"
                  autoComplete="firstName"
                  name="firstName"
                  register={register}
                />
                <AuthInputText
                  label="Last Name"
                  value={lastNameValue}
                  errors={errors}
                  placeholder="Last Name"
                  autoComplete="lastName"
                  name="lastName"
                  register={register}
                />
              </div>
              <AuthInputText
                label="Mobile Number"
                value={phoneNumberValue}
                errors={errors}
                placeholder="Enter Your Mobile Number"
                autoComplete="mobileNumber"
                name="mobileNumber"
                type="number"
                maxLength={10}
                register={register}
              />
              {phoneAlreadyInUse}
              <AuthInputText
                label="Email"
                value={emailValue || ""}
                errors={errors}
                placeholder="Example@email.com"
                autoComplete="emailId"
                name="emailId"
                register={register}
              />
              {emailAlreadyInUse}
              <AuthInputText
                label="Organization Name"
                value={organization || ""}
                errors={errors}
                placeholder="Kitti"
                autoComplete="organization"
                name="organization"
                register={register}
              />
              <AuthInputText
                label="Password"
                value={passwordValue}
                errors={errors}
                placeholder="Please enter your password"
                autoComplete="password"
                name="password"
                type="password"
                register={register}
              />
              <AuthInputText
                label="Confirm Password"
                value={confirmPassword}
                errors={errors}
                placeholder="Please enter your password"
                autoComplete="confirmPassword"
                name="confirmPassword"
                type="password"
                register={register}
              />
              <div className="flex items-center w-full justify-center">
                <div id="recaptcha-container"></div>
              </div>
              <div>
                <p className="text-center mt-2 text-[16px]">
                  Already have an account? <span className="text-[#444FBD] font-[600] cursor-pointer" onClick={() => navigate("/login")}>Sign In</span>
                </p>
              </div>
              <div className="mx-6">
                <button
                  type="submit"
                  disabled={!isButtonEnabled || isSubmitting}
                  className={`mt-4 p-2 w-full h-[50px] font-[400] text-[16px] leading-[22.96px] tracking-[1%] bg-[#444FBD] rounded-[20px] text-white rounded ${(!isButtonEnabled || isSubmitting) ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isSubmitting ? "Submitting..." : "Create Account"}
                </button>
                <p className="text-[12px] font-[500] mt-2 text-center items-center">
                  <input type='checkbox' checked className='mr-1' />By signing up, you agree to our{" "}
                  <a href="/terms" className="text-[#444FBD] hover:underline">T&C</a>,{" "}
                  <a href="/privacy" className="text-[#444FBD] hover:underline">Privacy Policy</a>,
                  <a href="/cancellation&refund" className="text-[#444FBD] hover:underline"> Cancellation & Refund Policy</a> and <a href="/shipping&delivery" className="text-[#444FBD] hover:underline">Shipping & Delivery Policy</a>.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthContainer>
  );
};

export default SignUp;